import { useState, useEffect, useRef, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { filterShipmentsByWarehouse, filterByWarehouse } from '@/lib/warehouseFilter';
import { useShipments } from '@/hooks/useShipments';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useTrips } from '@/hooks/useTrips';
import { QRCodeDisplay, generateShipmentQRValue } from '@/components/qr';
import { CopyButton } from '@/components/CopyButton';
import {
  Package,
  Plus,
  Search,
  MapPin,
  User,
  Building2,
  Truck,
  Edit,
  Trash2,
  Calendar,
  Weight,
  Box,
  Filter,
  X,
  FileText,
  Printer,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  List
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MultiItemShipmentForm } from '@/components/shipments/MultiItemShipmentForm';

interface ShipmentItem {
  id: string;
  itemNumber: number;
  description: string;
  quantity: number;
  quantityUnit: string;
  weight: number;
  volume: number;
  boxes: number;
}

interface Shipment {
  id: string;
  shipmentNumber: string;
  customerCode: string;
  customerName: string;
  supplierCode?: string;
  supplierName?: string;
  originWarehouse: string;
  destinationWarehouse: string;
  tripName: string;
  freightMethod: 'Air' | 'Sea';
  weight: number;
  volume: number;
  packages: number;
  status: 'On the Way to Warehouse' | 'Processing' | 'In Transit' | 'At Destination' | 'Delivered';
  description: string;
  items?: ShipmentItem[];
  createdAt: string;
  estimatedDelivery: string;
  sourcingRequestNumber?: string;
  statusHistory: {
    status: string;
    timestamp: string;
    note?: string;
  }[];
}

interface ShipmentsProps {
  filter?: 'all' | 'on-the-way' | 'processing' | 'in-transit' | 'at-destination' | 'delivered';
}

export default function Shipments(props: ShipmentsProps) {
  const { user } = useUser();
  const { t } = useLanguage();
  const { filter = 'all' } = props;

  const { shipments: apiShipments, isLoading: shipmentsLoading, createShipment, updateShipment, deleteShipment } = useShipments();
  const { customers: apiCustomers } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { trips: apiTrips } = useTrips();

  const allShipments = (apiShipments || []).map((s: any) => ({
    ...s,
    weight: Number(s.weight) || 0,
    volume: Number(s.volume) || 0,
    packages: Number(s.packages) || 0,
    createdAt: s.created_at || s.createdAt || '2024-01-01',
    sourcingRequestNumber: s.sourcing_request_number || s.sourcingRequestNumber || null,
    statusHistory: s.statusHistory || [
      { status: s.status || 'Processing', timestamp: s.created_at || s.createdAt || new Date().toISOString(), note: s.description || s.notes || 'Shipment created' }
    ]
  }));

  const customers = apiCustomers || [];
  const warehouses = apiWarehouses || [];
  const trips = apiTrips || [];

  // Old localStorage state - keeping for compatibility but not using
  const [, ] = useState<Shipment[]>(() => {
    const saved = localStorage.getItem('shiphub-shipments');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed;
      } catch (e) {
        console.error('Error parsing shipments from localStorage:', e);
        localStorage.removeItem('shiphub-shipments');
      }
    }

    // If no saved data, start with sample data
    return [
      {
        id: '1',
        shipmentNumber: 'SH-1234-56789',
        customerCode: '1234',
        customerName: 'John Doe',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'MOROCCO',
        tripName: 'GUANGZHOU-MOROCCO-001',
        freightMethod: 'Sea',
        weight: 150.5,
        volume: 2.3,
        packages: 5,
        status: 'Processing',
        description: 'Sample shipment 1',
        createdAt: '2024-01-15',
        estimatedDelivery: '2024-01-20',
        statusHistory: [
          { status: 'Processing', timestamp: '2024-01-15T10:00:00.000Z', note: 'Shipment created' }
        ]
      },
      {
        id: '2',
        shipmentNumber: 'SH-5678-12345',
        customerCode: '5678',
        customerName: 'Jane Smith',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'USA',
        tripName: 'GUANGZHOU-USA-002',
        freightMethod: 'Air',
        weight: 75.2,
        volume: 1.1,
        packages: 3,
        status: 'In Transit',
        description: 'Sample shipment 2',
        createdAt: '2024-01-16',
        estimatedDelivery: '2024-01-18',
        statusHistory: [
          { status: 'Processing', timestamp: '2024-01-16T09:00:00.000Z', note: 'Shipment created' },
          { status: 'In Transit', timestamp: '2024-01-17T10:00:00.000Z', note: 'Shipment departed' }
        ]
      },
      {
        id: '3',
        shipmentNumber: 'SH-9012-34567',
        customerCode: '9012',
        customerName: 'Bob Johnson',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'MOROCCO',
        tripName: 'GUANGZHOU-MOROCCO-001',
        freightMethod: 'Sea',
        weight: 200.8,
        volume: 3.5,
        packages: 8,
        status: 'At Destination',
        description: 'Sample shipment 3',
        createdAt: '2024-01-14',
        estimatedDelivery: '2024-01-19',
        statusHistory: [
          { status: 'Processing', timestamp: '2024-01-14T08:00:00.000Z', note: 'Shipment created' },
          { status: 'In Transit', timestamp: '2024-01-15T09:00:00.000Z', note: 'Shipment departed' },
          { status: 'At Destination', timestamp: '2024-01-18T14:00:00.000Z', note: 'Arrived at destination' }
        ]
      },
      {
        id: '4',
        shipmentNumber: 'SH-3456-78901',
        customerCode: '3456',
        customerName: 'Alice Brown',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'USA',
        tripName: 'GUANGZHOU-USA-002',
        freightMethod: 'Air',
        weight: 45.3,
        volume: 0.8,
        packages: 2,
        status: 'Delivered',
        description: 'Sample shipment 4',
        createdAt: '2024-01-13',
        estimatedDelivery: '2024-01-17',
        statusHistory: [
          { status: 'Processing', timestamp: '2024-01-13T11:00:00.000Z', note: 'Shipment created' },
          { status: 'In Transit', timestamp: '2024-01-14T12:00:00.000Z', note: 'Shipment departed' },
          { status: 'At Destination', timestamp: '2024-01-16T15:00:00.000Z', note: 'Arrived at destination' },
          { status: 'Delivered', timestamp: '2024-01-17T16:00:00.000Z', note: 'Delivered to customer' }
        ]
      }
    ];

  });

  // Filter shipments based on user's warehouse access
  const shipments = useMemo(() => {
    return filterByWarehouse(allShipments, user);
  }, [allShipments, user]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedShipmentForDetail, setSelectedShipmentForDetail] = useState<Shipment | null>(null);
  const [editingShipment, setEditingShipment] = useState<Shipment | null>(null);
  const [selectedShipmentForStatus, setSelectedShipmentForStatus] = useState<Shipment | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [newShipment, setNewShipment] = useState({
    customerCode: '',
    customerName: '',
    originWarehouse: '',
    destinationWarehouse: '',
    tripName: '',
    freightMethod: 'Air' as 'Air' | 'Sea',
    weight: '',
    volume: '',
    packages: '',
    description: ''
  });

  // Smart customer search states
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  // Save shipments to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('shiphub-shipments', JSON.stringify(allShipments));
  }, [allShipments]);

  // Filter shipments based on user role
  const customerFilteredShipments = useMemo(() => {
    // If user is customer, show only their shipments
    if (user?.role === 'customer') {
      return allShipments.filter(shipment => shipment.customerCode === user.customerCode);
    }

    // For all other users (admin, warehouse), use the same filter that works for trips
    return filterByWarehouse(allShipments, user);
  }, [allShipments, user]);

  // Function to clean up orphaned shipments from trips and customers
  const cleanupOrphanedShipments = () => {
    const currentShipmentNumbers = new Set(allShipments.map(s => s.shipmentNumber));
    console.log('🔍 Shipments Cleanup - Current shipments:', allShipments.length);
    console.log('🔍 Shipments Cleanup - Current shipment numbers:', Array.from(currentShipmentNumbers));

    // Clean up trips
    const savedTrips = localStorage.getItem('shiphub-trips');
    if (savedTrips) {
      try {
        const tripsData = JSON.parse(savedTrips);
        const cleanedTrips = tripsData.map((trip: any) => {
          if (trip.shipments && Array.isArray(trip.shipments)) {
            const validShipments = trip.shipments.filter((tripShipment: any) =>
              currentShipmentNumbers.has(tripShipment.shipmentNumber)
            );

            // Recalculate totals
            const totalWeight = validShipments.reduce((sum: number, shipment: any) => sum + (shipment.weight || 0), 0);
            const totalVolume = validShipments.reduce((sum: number, shipment: any) => sum + (shipment.volume || 0), 0);
            const totalPackages = validShipments.reduce((sum: number, shipment: any) => sum + (shipment.packages || 0), 0);

            return {
              ...trip,
              shipments: validShipments,
              totalWeight,
              totalVolume,
              totalPackages
            };
          }
          return trip;
        });
        localStorage.setItem('shiphub-trips', JSON.stringify(cleanedTrips));
      } catch (e) {
        console.error('Error cleaning up trips:', e);
      }
    }

    // Clean up customer counts
    const savedCustomers = localStorage.getItem('shiphub-customers');
    if (savedCustomers) {
      try {
        const customersData = JSON.parse(savedCustomers);
        const cleanedCustomers = customersData.map((customer: any) => {
          const customerShipments = shipments.filter(s => s.customerCode === customer.code);
          return {
            ...customer,
            totalShipments: customerShipments.length
          };
        });
        localStorage.setItem('shiphub-customers', JSON.stringify(cleanedCustomers));
      } catch (e) {
        console.error('Error cleaning up customers:', e);
      }
    }
  };

  // Cleanup disabled to prevent shipment deletion issues
  // useEffect(() => {
  //   console.log('🔍 Shipments Page - Running cleanup on mount');
  //   cleanupOrphanedShipments();
  // Removed - already defined from API hooks above

  // Update trips when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('shiphub-trips');
      setTrips(saved ? JSON.parse(saved) : []);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Smart customer search with progressive filtering
  const handleCustomerCodeChange = (code: string) => {
    if (code.length === 0) {
      setShowCustomerDropdown(false);
      setFilteredCustomers([]);
      setSelectedCustomer(null);
      setNewShipment({...newShipment, customerCode: '', customerName: ''});
      return;
    }

    // Filter customers based on warehouse access
    // Warehouse managers can only see customers assigned to their warehouse
    const accessibleCustomers = user?.role === 'admin' 
      ? customers 
      : customers.filter(customer => 
          customer.destination_warehouse === user?.warehouseCode || 
          customer.destination_warehouse === user?.warehouseName
        );

    // Filter customers based on current input (only from accessible customers)
    const matchingCustomers = accessibleCustomers.filter(customer =>
      customer.code.toLowerCase().startsWith(code.toLowerCase())
    );

    setFilteredCustomers(matchingCustomers);
    setShowCustomerDropdown(matchingCustomers.length > 0);

    // Auto-select if exact match in allowed customers
    const exactMatch = matchingCustomers.find(customer => customer.code === code);
    if (exactMatch) {
      setSelectedCustomer(exactMatch);
      setNewShipment({...newShipment, customerCode: code, customerName: exactMatch.name});
      setShowCustomerDropdown(false);
    } else {
      // If no match in allowed customers, clear customer name
      setSelectedCustomer(null);
      setNewShipment({...newShipment, customerCode: code, customerName: ''});
    }
  };

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer: any) => {
    setSelectedCustomer(customer);
    setNewShipment({
      ...newShipment,
      customerCode: customer.code,
      customerName: customer.name
    });
    setShowCustomerDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get warehouse name by code
  const getWarehouseName = (code: string) => {
    const warehouse = warehouses.find(w => w.code === code);
    return warehouse ? warehouse.name : code;
  };

  // Get available trips (only those with "Planning" status)
  const getAvailableTrips = () => {
    return trips.filter(trip =>
      trip.status === 'Planning' &&
      (user?.role === 'admin' ||
       trip.destinationWarehouse === user?.warehouseCode ||
       trip.destinationWarehouse === user?.warehouseName ||
       trip.originWarehouse === user?.warehouseCode ||
       trip.originWarehouse === user?.warehouseName)
    );
  };

  // Handle trip selection and auto-fill all trip-related fields
  const handleTripChange = (tripName: string) => {
    const trip = trips.find(t => t.name === tripName);
    if (trip) {
      setNewShipment({
        ...newShipment,
        tripName: tripName,
        originWarehouse: trip.originWarehouse || trip.origin || '',
        destinationWarehouse: trip.destinationWarehouse || trip.destination || '',
        freightMethod: trip.freightMethod || 'Air' // Use trip's freight method or default to Air
      });
    } else {
      setNewShipment({...newShipment, tripName: tripName});
    }
  };

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('all');
  const [tripFilter, setTripFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Get unique values for filters
  // Get unique warehouses from shipments (avoid duplicates from origin/destination)
  const uniqueWarehouses = Array.from(new Set(
    customerFilteredShipments
      .map(s => [s.originWarehouse, s.destinationWarehouse]) // Get both origin and destination
      .flat() // Flatten the array
      .filter(Boolean) // Remove empty values
  ));

  // Apply role-based filtering after creating unique list
  const filteredUniqueWarehouses = user?.role === 'admin'
    ? uniqueWarehouses
    : uniqueWarehouses.filter(warehouse => warehouse === user?.warehouseCode);

  // Get warehouse names for display
  const getWarehouseDisplayNames = () => {
    return filteredUniqueWarehouses.map(warehouseCode => {
      const warehouse = warehouses.find(w => w.code === warehouseCode);
      return {
        code: warehouseCode,
        name: warehouse ? warehouse.name : warehouseCode // Fallback to code if name not found
      };
    });
  };

  const warehouseDisplayNames = getWarehouseDisplayNames();

  const uniqueTrips = Array.from(new Set(customerFilteredShipments.map(s => s.tripName || ''))).filter(Boolean);

  // Get active filter count
  const activeFilterCount = [
    statusFilter !== 'all' ? 1 : 0,
    warehouseFilter !== 'all' ? 1 : 0,
    tripFilter !== 'all' ? 1 : 0,
    searchTerm.trim() !== '' ? 1 : 0
  ].reduce((sum, count) => sum + count, 0);

  // Reset filters
  const resetFilters = () => {
    setStatusFilter('all');
    setWarehouseFilter('all');
    setTripFilter('all');
    setSearchTerm('');
  };

  const generateShipmentNumber = (customerCode: string) => {
    // Generate 5-digit random number
    const fiveDigitNumber = Math.floor(10000 + Math.random() * 90000).toString();
    return `SH-${customerCode}-${fiveDigitNumber}`;
  };

  const filteredShipments = customerFilteredShipments.filter(shipment => {
    // Safe access to all properties with fallback values
    const shipmentNumber = (shipment.shipmentNumber || '').toLowerCase();
    const customerName = (shipment.customerName || '').toLowerCase();
    const customerCode = (shipment.customerCode || '');
    const tripName = (shipment.tripName || ''); // Keep original case for filter matching
    const tripNameLower = tripName.toLowerCase(); // Lowercase for search
    const description = (shipment.description || '').toLowerCase();
    const originWarehouse = shipment.originWarehouse || '';
    const destinationWarehouse = shipment.destinationWarehouse || '';
    const status = shipment.status || '';

    // Convert search term to lowercase once
    const searchLower = searchTerm.toLowerCase();

    // Search term matching (case-insensitive)
    const searchMatch =
      shipmentNumber.includes(searchLower) ||
      customerName.includes(searchLower) ||
      customerCode.includes(searchTerm) || // customerCode is not case-sensitive
      tripNameLower.includes(searchLower) ||
      description.includes(searchLower);

    // Status filter matching - check both URL parameter AND dropdown filter
    let statusMatch = true;
    
    // URL parameter filter
    if (filter === 'on-the-way') {
      statusMatch = status === 'On the Way to Warehouse';
    } else if (filter === 'processing') {
      statusMatch = status === 'Processing';
    } else if (filter === 'in-transit') {
      statusMatch = status === 'In Transit';
    } else if (filter === 'at-destination') {
      statusMatch = status === 'At Destination';
    } else if (filter === 'delivered') {
      statusMatch = status === 'Delivered';
    }
    
    // Dropdown status filter (if not 'all')
    if (statusFilter !== 'all' && statusMatch) {
      statusMatch = status === statusFilter;
    }

    // Warehouse filter matching (origin or destination)
    const warehouseMatch = warehouseFilter === 'all' ||
      originWarehouse === warehouseFilter ||
      destinationWarehouse === warehouseFilter;

    // Trip filter matching (case-sensitive to match exact trip names)
    const tripMatch = tripFilter === 'all' || tripName === tripFilter;

    // Date range filter
    let dateMatch = true;
    if (dateRange.startDate && dateRange.endDate && shipment.createdAt) {
      const shipmentDate = new Date(shipment.createdAt);
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999); // Include end date fully
      dateMatch = shipmentDate >= start && shipmentDate <= end;
    }

    return searchMatch && statusMatch && warehouseMatch && tripMatch && dateMatch;
  }).sort((a, b) => {
    const dateA = new Date(a.createdAt || '1970-01-01').getTime();
    const dateB = new Date(b.createdAt || '1970-01-01').getTime();
    return dateB - dateA;
  });

  const handleAddShipment = () => {
    // Validate customer is in the allowed list for warehouse users
    if (user?.role === 'warehouse') {
      const isValidCustomer = customers.some(c => c.code === newShipment.customerCode);
      if (!isValidCustomer) {
        alert('Invalid customer code. You can only create shipments for customers in your warehouse area.');
        return;
      }
    }

    createShipment({
      customer_code: newShipment.customerCode,
      customer_name: newShipment.customerName,
      origin_warehouse: newShipment.originWarehouse,
      destination_warehouse: newShipment.destinationWarehouse,
      trip_name: newShipment.tripName,
      freight_method: newShipment.freightMethod,
      weight: parseFloat(newShipment.weight) || 0,
      volume: parseFloat(newShipment.volume) || 0,
      packages: parseInt(newShipment.packages) || 0,
      status: 'Processing',
      description: newShipment.description,
      estimated_delivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });

    // Reset form
    setNewShipment({
      customerCode: '',
      customerName: '',
      originWarehouse: '',
      destinationWarehouse: '',
      tripName: '',
      freightMethod: 'Air' as 'Air' | 'Sea',
      weight: '',
      volume: '',
      packages: '',
      description: ''
    });
    setIsAddDialogOpen(false);
  };

  // Edit shipment function
  const handleEditShipment = () => {
    if (!editingShipment) return;

    updateShipment({
      shipment_number: editingShipment.shipmentNumber,
      customer_name: editingShipment.customerName,
      origin_warehouse: editingShipment.originWarehouse,
      destination_warehouse: editingShipment.destinationWarehouse,
      trip_name: editingShipment.tripName,
      freight_method: editingShipment.freightMethod,
      weight: editingShipment.weight,
      volume: editingShipment.volume,
      packages: editingShipment.packages,
      status: editingShipment.status,
      description: editingShipment.description,
      estimated_delivery: editingShipment.estimatedDelivery
    });

    setEditingShipment(null);
    setIsEditDialogOpen(false);
  };

  // Delete shipment function
  const handleDeleteShipment = (shipmentNumber: string) => {
    deleteShipment(shipmentNumber);
  };

  // Update shipment status
  const handleUpdateStatus = () => {
    if (!selectedShipmentForStatus || !newStatus) return;

    // Build updated status history
    const newStatusHistory = [
      ...(selectedShipmentForStatus.statusHistory || []),
      {
        status: newStatus,
        timestamp: new Date().toISOString(),
        note: statusNote || `Status changed to ${newStatus}`
      }
    ];

    updateShipment({
      shipment_number: selectedShipmentForStatus.shipmentNumber,
      status: newStatus,
      status_note: statusNote || `Status changed to ${newStatus}`,
      status_history: JSON.stringify(newStatusHistory)
    });

    setSelectedShipmentForStatus(null);
    setNewStatus('');
    setStatusNote('');
    setIsStatusDialogOpen(false);
  };

  // Open status update dialog
  const openStatusDialog = (shipment: Shipment) => {
    setSelectedShipmentForStatus(shipment);
    setNewStatus(shipment.status);
    setStatusNote('');
    setIsStatusDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'On the Way to Warehouse': return 'bg-orange-100 text-orange-800';
      case 'Processing': return 'bg-yellow-100 text-yellow-800';
      case 'In Transit': return 'bg-blue-100 text-blue-800';
      case 'At Destination': return 'bg-purple-100 text-purple-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'On the Way to Warehouse': return <Package className="h-4 w-4 text-orange-600" />;
      case 'Processing': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'In Transit': return <Truck className="h-4 w-4 text-blue-600" />;
      case 'At Destination': return <MapPin className="h-4 w-4 text-purple-600" />;
      case 'Delivered': return <CheckCircle className="h-4 w-4 text-green-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  // Download shipment info
  const handleDownloadPDF = (shipment: Shipment) => {
    const logoUrl = window.location.origin + '/logo-full.jpg';
    
    const pdfContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Shipment Invoice - ${shipment.shipmentNumber}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      @page {
        size: A4 portrait;
        margin: 10mm;
      }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        line-height: 1.4;
        color: #1a1a2e;
        background: #fff;
        padding: 12px;
      }

      /* HEADER */
      .doc-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding-bottom: 8px;
        margin-bottom: 12px;
        border-bottom: 2px solid #1e3a6e;
      }
      .doc-header img.logo {
        height: 50px;
        width: auto;
        object-fit: contain;
        flex-shrink: 0;
      }
      .doc-header .vertical-line {
        width: 2px;
        height: 50px;
        background: #1e3a6e;
        flex-shrink: 0;
      }
      .doc-header .company-info {
        flex: 1;
        font-size: 8pt;
        color: #555;
        line-height: 1.5;
      }

      /* TITLE BAND */
      .title-band {
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        color: #fff;
        padding: 8px 12px;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
      .title-band .meta { text-align: right; font-size: 8pt; line-height: 1.5; }
      .title-band .meta strong { font-size: 10pt; display: block; }

      /* INFO BOXES */
      .info-row {
        display: flex;
        gap: 10px;
        margin-bottom: 12px;
      }
      .info-box {
        flex: 1;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 8px 10px;
        background: #f8f9fa;
        page-break-inside: avoid;
      }
      .info-box h3 {
        font-size: 8pt;
        text-transform: uppercase;
        color: #1e3a6e;
        border-bottom: 1px solid #1e3a6e;
        padding-bottom: 3px;
        margin-bottom: 6px;
        letter-spacing: 0.5px;
      }
      .info-box p { font-size: 8.5pt; margin-bottom: 2px; }

      /* SECTION TITLE */
      .section-title {
        font-size: 9pt;
        font-weight: bold;
        color: #fff;
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        padding: 4px 8px;
        border-radius: 3px;
        margin: 12px 0 6px 0;
      }

      /* TABLES */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 7.5pt;
        page-break-inside: auto;
      }
      thead tr {
        background: #1e3a6e;
        color: #fff;
      }
      th {
        padding: 5px 6px;
        text-align: left;
        font-weight: 600;
        font-size: 8pt;
      }
      td {
        padding: 4px 6px;
        border-bottom: 1px solid #e9ecef;
      }
      tbody tr:nth-child(even) { background: #f8f9fa; }
      td.right, th.right { text-align: right; }
      tr { page-break-inside: avoid; }

      /* TOTALS BOX */
      .totals-box {
        margin-top: 12px;
        padding: 10px;
        border: 2px solid #1e3a6e;
        border-radius: 4px;
        background: #f8f9fa;
        max-width: 300px;
        margin-left: auto;
        page-break-inside: avoid;
      }
      .totals-box table { margin: 0; font-size: 8pt; }
      .totals-box tr { border-bottom: 1px solid #dee2e6; }
      .totals-box tr:last-child { border-bottom: none; }
      .totals-box th {
        background: none;
        color: #555;
        padding: 5px 6px;
        font-weight: 600;
        text-align: left;
      }
      .totals-box td {
        padding: 5px 6px;
        border: none;
        text-align: right;
        font-weight: bold;
        color: #1e3a6e;
      }

      /* FOOTER */
      .doc-footer {
        margin-top: 20px;
        padding-top: 8px;
        border-top: 1px solid #dee2e6;
        text-align: center;
        font-size: 7.5pt;
        color: #888;
        line-height: 1.6;
        page-break-inside: avoid;
      }
      .doc-footer strong { color: #1e3a6e; }

      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
</head>
<body>

    <!-- HEADER -->
    <div class="doc-header">
      <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
      <div class="vertical-line"></div>
      <div class="company-info">
        Phone: +86 130 4806 4436<br/>
        Email: sales@sourcemygoods.com<br/>
        Address: Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China
      </div>
    </div>

    <!-- TITLE BAND -->
    <div class="title-band">
      <h1>SHIPMENT DETAILS</h1>
      <div class="meta">
        <strong>${shipment.shipmentNumber}</strong>
        <span>${new Date().toLocaleDateString()}</span>
      </div>
    </div>

    <!-- INFO ROW -->
    <div class="info-row">
      <div class="info-box">
        <h3>Customer Information</h3>
        <p><strong>${shipment.customerName}</strong></p>
        <p>Code: ${shipment.customerCode}</p>
        ${shipment.customerEmail ? `<p>Email: ${shipment.customerEmail}</p>` : ''}
      </div>
      <div class="info-box">
        <h3>Shipment Details</h3>
        <p><strong>Status:</strong> ${shipment.status}</p>
        <p><strong>Freight:</strong> ${shipment.freightMethod || 'Air'}</p>
        <p><strong>Trip:</strong> ${shipment.tripName || '—'}</p>
      </div>
      <div class="info-box">
        <h3>Route</h3>
        <p><strong>From:</strong> ${shipment.originWarehouse}</p>
        <p><strong>To:</strong> ${shipment.destinationWarehouse}</p>
      </div>
    </div>

    <!-- ITEMS TABLE -->
    ${shipment.items && shipment.items.length > 0 ? `
      <div class="section-title">Items Breakdown</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Description</th>
            <th class="right">Quantity</th>
            <th class="right">Weight (kg)</th>
            <th class="right">Volume (m³)</th>
            <th class="right">Boxes</th>
          </tr>
        </thead>
        <tbody>
          ${shipment.items.map((item, idx) => `
            <tr>
              <td>${item.itemNumber || idx + 1}</td>
              <td><strong>${item.description}</strong></td>
              <td class="right">${item.quantity} ${item.quantityUnit}</td>
              <td class="right">${item.weight}</td>
              <td class="right">${item.volume}</td>
              <td class="right">${item.boxes}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- TOTALS BOX -->
      <div class="totals-box">
        <table>
          <tr>
            <th>Total Weight:</th>
            <td>${shipment.weight} kg</td>
          </tr>
          <tr>
            <th>Total Volume:</th>
            <td>${shipment.volume} m³</td>
          </tr>
          <tr>
            <th>Total Packages:</th>
            <td>${shipment.packages}</td>
          </tr>
        </table>
      </div>
    ` : `
      <div class="section-title">Shipment Summary</div>
      <div class="info-box">
        <p><strong>Weight:</strong> ${shipment.weight} kg</p>
        <p><strong>Volume:</strong> ${shipment.volume} m³</p>
        <p><strong>Packages:</strong> ${shipment.packages}</p>
        <p><strong>Description:</strong> ${shipment.description}</p>
      </div>
    `}

    <!-- FOOTER -->
    <div class="doc-footer">
      <strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping<br/>
      Operated by Trust Exchange International Trading LTD<br/>
      Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
    </div>

    <script>window.onload = function() { window.print(); }</script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(pdfContent);
      printWindow.document.close();
    }
  };

  // Print shipping label
  const handlePrintLabel = (shipment: Shipment) => {
    // Generate QR code as base64 image
    const qrValue = generateShipmentQRValue(shipment.shipmentNumber);
    const canvas = document.createElement('canvas');
    
    // Also generate sourcing request QR if exists
    const hasSourceRequest = !!shipment.sourcingRequestNumber;
    const srQrValue = hasSourceRequest ? `request:${shipment.sourcingRequestNumber}` : '';
    const srCanvas = document.createElement('canvas');
    
    import('qrcode').then(QRCode => {
      // Generate shipment QR
      QRCode.default.toCanvas(canvas, qrValue, { width: 150, margin: 1 }, (error) => {
        if (error) console.error(error);
        const qrImageUrl = canvas.toDataURL();
        
        // Generate sourcing request QR if exists
        const generateLabel = (srQrImageUrl: string) => {

        const logoUrl = window.location.origin + '/logo-icon.jpg';
        const printContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Shipping Label - ${shipment.shipmentNumber}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }

        /* Thermal label: 76mm × 130mm */
        @page {
            size: 76mm 130mm;
            margin: 0;
        }

        body {
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
            width: 76mm;
            min-height: 130mm;
            margin: 0;
            padding: 0;
        }

        .label {
            width: 76mm;
            min-height: 130mm;
            border: 2px solid #1e3a6e;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }

        /* ── TOP HEADER BAND ── */
        .label-header {
            background: #1e3a6e;
            color: #fff;
            padding: 4px 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
        }
        .label-header img.logo-icon {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 1.5px solid rgba(255,255,255,0.5);
            object-fit: cover;
        }
        .label-header .co-name {
            font-size: 9pt;
            font-weight: bold;
        }
        .label-header .co-website {
            font-size: 7pt;
            color: rgba(255,255,255,0.9);
        }
        .label-header .freight-badge {
            background: #f5a623;
            color: #fff;
            font-size: 7pt;
            font-weight: bold;
            padding: 2px 6px;
            border-radius: 8px;
            text-transform: uppercase;
        }

        /* ── SHIPMENT NUMBER ── */
        .shipment-no {
            background: #f0f4ff;
            border-bottom: 2px solid #1e3a6e;
            text-align: center;
            padding: 5px 4px;
            font-size: 14pt;
            font-weight: bold;
            letter-spacing: 2px;
            color: #1e3a6e;
        }

        /* ── QR SECTION (centered) ── */
        .qr-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 6px 6px 4px 6px;
            border-bottom: 1.5px solid #dee2e6;
        }
        .qr-section img {
            width: 90px;
            height: 90px;
            display: block;
            margin: 0 auto;
        }
        .qr-section .scan-hint {
            font-size: 6.5pt;
            color: #888;
            margin-top: 3px;
            text-transform: uppercase;
            letter-spacing: 1px;
        }

        /* ── INFO ROWS ── */
        .info-grid {
            flex: 1;
            padding: 6px 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        .info-row {
            display: flex;
            gap: 6px;
            align-items: baseline;
            font-size: 9pt;
            border-bottom: 1px dashed #e0e0e0;
            padding-bottom: 3px;
        }
        .info-row:last-child { border-bottom: none; }
        .info-lbl {
            font-weight: bold;
            color: #1e3a6e;
            min-width: 72px;
            font-size: 8pt;
            text-transform: uppercase;
        }
        .info-val { color: #222; }

        /* ── ROUTE BOX ── */
        .route-box {
            background: #f8f9fa;
            border-top: 2px solid #1e3a6e;
            padding: 5px 8px;
        }
        .route-title {
            font-size: 7pt;
            font-weight: bold;
            text-transform: uppercase;
            color: #1e3a6e;
            text-align: center;
            margin-bottom: 4px;
            letter-spacing: 1px;
        }
        .route-arrow {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 8.5pt;
        }
        .route-point {
            flex: 1;
            padding: 3px 5px;
            background: #fff;
            border: 1px solid #dee2e6;
            border-radius: 3px;
            text-align: center;
        }
        .route-point .rp-label { font-size: 6.5pt; color: #888; text-transform: uppercase; }
        .route-point .rp-name  { font-size: 8pt; font-weight: bold; color: #1e3a6e; }
        .arrow-icon { color: #f5a623; font-size: 12pt; font-weight: bold; flex-shrink:0; }
        
        /* ── PRINT DATE ── */
        .print-date {
            font-size: 6pt;
            color: #999;
            text-align: center;
            padding: 3px 0;
            border-top: 1px solid #e0e0e0;
        }

        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
    </style>
</head>
<body>
<div class="label">

    <!-- TOP BAND -->
    <div class="label-header">
        <img class="logo-icon" src="${logoUrl}" alt="SMG"
             onerror="this.style.display='none'"/>
        <span class="co-name">SourceMyGoods</span>
        <span class="co-website">www.sourcemygoods.com</span>
        <span class="freight-badge">${shipment.freightMethod}</span>
    </div>

    <!-- SHIPMENT NUMBER -->
    <div class="shipment-no">${shipment.shipmentNumber}</div>

    <!-- QR CODE – centered (Shipment QR only) -->
    <div class="qr-section">
        <img src="${qrImageUrl}" alt="QR Code"/>
        <div class="scan-hint">Scan to update status</div>
    </div>

    <!-- INFO ROWS -->
    <div class="info-grid">
        <div class="info-row">
            <span class="info-lbl">Customer</span>
            <span class="info-val">${shipment.customerCode}</span>
        </div>
        <div class="info-row">
            <span class="info-lbl">Trip</span>
            <span class="info-val">${shipment.tripName || '—'}</span>
        </div>
        <div class="info-row">
            <span class="info-lbl">Weight</span>
            <span class="info-val">${shipment.weight} kg</span>
        </div>
        <div class="info-row">
            <span class="info-lbl">Volume</span>
            <span class="info-val">${shipment.volume} m³</span>
        </div>
        <div class="info-row">
            <span class="info-lbl">Packages</span>
            <span class="info-val">${shipment.packages}</span>
        </div>
    </div>

    <!-- ROUTE BOX -->
    <div class="route-box">
        <div class="route-title">Route</div>
        <div class="route-arrow">
            <div class="route-point">
                <div class="rp-label">From</div>
                <div class="rp-name">${getWarehouseName(shipment.originWarehouse)}</div>
            </div>
            <div class="arrow-icon">→</div>
            <div class="route-point">
                <div class="rp-label">To</div>
                <div class="rp-name">${getWarehouseName(shipment.destinationWarehouse)}</div>
            </div>
        </div>
    </div>
    
    ${hasSourceRequest ? `
    <!-- SOURCING REQUEST SECTION -->
    <div style="border-top: 2px dashed #1e3a6e; padding: 4px 6px; background: #f0f9ff;">
        <div style="display: flex; align-items: center; gap: 6px;">
            <img src="${srQrImageUrl}" alt="Request QR" style="width: 45px; height: 45px; flex-shrink: 0;"/>
            <div style="flex: 1; min-width: 0;">
                <div style="font-size: 6pt; font-weight: bold; color: #1e3a6e; text-transform: uppercase; letter-spacing: 0.5px;">Sourcing Request</div>
                <div style="font-size: 8pt; font-weight: bold; color: #0369a1; margin-top: 1px;">${shipment.sourcingRequestNumber}</div>
                <div style="font-size: 5.5pt; color: #666;">Scan for order details</div>
            </div>
        </div>
    </div>
    ` : ''}

    <!-- PRINT DATE -->
    <div class="print-date">
        Printed: ${new Date().toLocaleString('en-US', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        })}
    </div>

</div>
<script>
    // Wait for images to load before printing
    window.onload = function() {
        const images = document.querySelectorAll('img');
        let loadedCount = 0;
        const totalImages = images.length;
        
        if (totalImages === 0) {
            window.print();
            return;
        }
        
        images.forEach(img => {
            if (img.complete) {
                loadedCount++;
            } else {
                img.onload = () => {
                    loadedCount++;
                    if (loadedCount === totalImages) {
                        setTimeout(() => window.print(), 100);
                    }
                };
                img.onerror = () => {
                    loadedCount++;
                    if (loadedCount === totalImages) {
                        setTimeout(() => window.print(), 100);
                    }
                };
            }
        });
        
        if (loadedCount === totalImages) {
            setTimeout(() => window.print(), 100);
        }
    };
</script>
</body>
</html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
        }
        }; // end generateLabel
        
        // If has sourcing request, generate its QR too
        if (hasSourceRequest) {
          QRCode.default.toCanvas(srCanvas, srQrValue, { width: 150, margin: 1 }, (err) => {
            if (err) console.error(err);
            const srQrImageUrl = srCanvas.toDataURL();
            generateLabel(srQrImageUrl);
          });
        } else {
          generateLabel('');
        }
      });
    });
  };

  // Open detailed view dialog
  const openDetailDialog = (shipment: Shipment) => {
    setSelectedShipmentForDetail(shipment);
    setIsDetailDialogOpen(true);
  };

  // Get customer details by code
  const getCustomerDetails = (customerCode: string) => {
    return customers.find(customer => customer.code === customerCode);
  };

  // Print commercial invoice
  const printCommercialInvoice = (shipment: Shipment) => {
    const customer = getCustomerDetails(shipment.customerCode);
    const originWarehouse = warehouses.find(w => w.code === shipment.originWarehouse);
    const destinationWarehouse = warehouses.find(w => w.code === shipment.destinationWarehouse);
    const mainWarehouse = warehouses.find(w => w.type === 'Main') || warehouses[0];

    // Hardcoded company address
    const fullAddress = 'Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China';

    const logoUrl = window.location.origin + '/logo-full.jpg';
    const printContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Shipment Details - ${shipment.shipmentNumber}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page {
          size: A4 portrait;
          margin: 12mm 12mm 15mm 12mm;
        }
        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11pt;
          line-height: 1.5;
          color: #1a1a2e;
          background: #fff;
        }

        /* HEADER */
        .doc-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding-bottom: 12px;
          border-bottom: 3px solid #1e3a6e;
          margin-bottom: 16px;
        }
        .doc-header img.logo {
          height: 60px;
          width: auto;
          object-fit: contain;
          flex-shrink: 0;
        }
        .doc-header .vertical-line {
          width: 3px;
          height: 60px;
          background: #1e3a6e;
          flex-shrink: 0;
        }
        .doc-header .company-info {
          flex: 1;
          font-size: 9pt;
          color: #555;
          line-height: 1.7;
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
        }
        .doc-header .company-info strong { 
          color: #1e3a6e; 
          font-size: 10pt;
          display: block;
          margin-bottom: 2px;
        }
        @media print {
          .doc-header .company-info {
            max-width: 100%;
            overflow: visible;
          }
        }

        /* TITLE BAND */
        .title-band {
          background: #1e3a6e;
          color: #fff;
          padding: 10px 16px;
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .title-band h1 { font-size: 20pt; letter-spacing: 2px; }
        .title-band .meta { 
          text-align: right; 
          font-size: 9pt; 
          line-height: 1.7; 
        }
        .title-band .meta strong { 
          font-size: 12pt; 
          display: block; 
        }

        /* INFO BOXES */
        .info-row {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }
        .info-box {
          flex: 1;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 10px 12px;
          background: #f8f9fa;
        }
        .info-box h3 {
          font-size: 9pt;
          text-transform: uppercase;
          color: #1e3a6e;
          border-bottom: 1px solid #1e3a6e;
          padding-bottom: 4px;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }
        .info-box p { 
          font-size: 10pt; 
          margin-bottom: 3px; 
        }

        /* SECTION TITLE */
        .section-title {
          font-size: 10pt;
          font-weight: bold;
          color: #fff;
          background: #1e3a6e;
          padding: 5px 10px;
          border-radius: 3px;
          margin: 16px 0 8px 0;
        }

        /* TABLES */
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9pt;
        }
        thead tr {
          background: #1e3a6e;
          color: #fff;
        }
        th {
          padding: 7px 8px;
          text-align: left;
          font-weight: 600;
        }
        td {
          padding: 6px 8px;
          border-bottom: 1px solid #e9ecef;
        }
        tbody tr { page-break-inside: avoid; }
        tbody tr:nth-child(even) { background: #f8f9fa; }
        th.right, td.right { text-align: right; }
        th.center, td.center { text-align: center; }

        /* TOTALS ROW */
        .totals-row {
          background: #e3f2fd !important;
          font-weight: bold;
        }

        /* STATUS HISTORY */
        .history-item {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          margin-bottom: 12px;
          padding: 8px;
          border-left: 3px solid #1e3a6e;
          background: #f8f9fa;
          page-break-inside: avoid;
        }
        .history-item:last-child { margin-bottom: 0; }
        .h-status {
          font-weight: bold;
          font-size: 10pt;
          color: #1e3a6e;
        }
        .h-time {
          font-size: 8pt;
          color: #666;
        }
        .h-note {
          font-size: 9pt;
          color: #555;
          margin-top: 4px;
          padding: 4px 8px;
          background: #fff;
          border-radius: 3px;
        }

        /* FOOTER */
        .doc-footer {
          margin-top: 24px;
          padding-top: 10px;
          border-top: 1px solid #dee2e6;
          text-align: center;
          font-size: 8pt;
          color: #888;
        }
        .doc-footer strong { color: #1e3a6e; }

        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
    </style>
</head>
<body>

    <!-- HEADER -->
    <div class="doc-header">
        <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
        <div class="vertical-line"></div>
        <div class="company-info">
            <strong>SourceMyGoods</strong><br/>
            Phone: ${mainWarehouse?.phone || '+86 130 4806 4436'}<br/>
            Email: ${mainWarehouse?.email || 'info@sourcemygoods.com'}<br/>
            Address: ${fullAddress}
        </div>
    </div>

    <!-- TITLE BAND -->
    <div class="title-band">
        <h1>SHIPMENT DETAILS</h1>
        <div class="meta">
            <strong>${shipment.shipmentNumber}</strong>
            Date: ${new Date().toLocaleDateString()}
        </div>
    </div>

    <!-- CUSTOMER & SHIPMENT INFO -->
    <div class="info-row">
        <div class="info-box">
            <h3>Customer Information</h3>
            <p><strong>Customer Code:</strong> ${shipment.customerCode}</p>
            <p><strong>Customer Name:</strong> ${shipment.customerName}</p>
            <p><strong>Contact:</strong> ${customer?.phone || 'N/A'}</p>
            <p><strong>Email:</strong> ${customer?.email || 'N/A'}</p>
        </div>

        <div class="info-box">
            <h3>Shipment Information</h3>
            <p><strong>Trip Name:</strong> ${shipment.tripName || 'Unassigned'}</p>
            <p><strong>Freight Method:</strong> ${shipment.freightMethod}</p>
            <p><strong>Status:</strong> ${shipment.status}</p>
            <p><strong>Created:</strong> ${shipment.createdAt}</p>
            ${shipment.sourcingRequestNumber ? `<p><strong>Sourcing Request:</strong> <span style="color: #0369a1; font-weight: bold;">${shipment.sourcingRequestNumber}</span></p>` : ''}
        </div>
    </div>

    <!-- ITEMS BREAKDOWN -->
    <div class="section-title">Items Breakdown</div>
    ${shipment.items && shipment.items.length > 0 ? `
        <table>
            <thead>
                <tr>
                    <th>#</th>
                    <th>Item Description</th>
                    <th class="center">Quantity</th>
                    <th class="right">Weight (kg)</th>
                    <th class="right">Volume (m³)</th>
                    <th class="right">Boxes</th>
                </tr>
            </thead>
            <tbody>
                ${shipment.items.map((item, idx) => `
                    <tr>
                        <td>${item.itemNumber || idx + 1}</td>
                        <td>${item.description}</td>
                        <td class="center">${item.quantity} ${item.quantityUnit}</td>
                        <td class="right">${item.weight.toFixed(1)}</td>
                        <td class="right">${item.volume.toFixed(2)}</td>
                        <td class="right">${item.boxes}</td>
                    </tr>
                `).join('')}
                <tr class="totals-row">
                    <td colspan="3" style="text-align:right;">TOTALS:</td>
                    <td class="right">${shipment.weight.toFixed(1)} kg</td>
                    <td class="right">${shipment.volume.toFixed(2)} m³</td>
                    <td class="right">${shipment.packages}</td>
                </tr>
            </tbody>
        </table>
    ` : `
        <table>
            <tbody>
                <tr>
                    <td><strong>Total Weight:</strong></td>
                    <td class="right">${shipment.weight} kg</td>
                </tr>
                <tr>
                    <td><strong>Total Volume:</strong></td>
                    <td class="right">${shipment.volume} m³</td>
                </tr>
                <tr>
                    <td><strong>Total Packages:</strong></td>
                    <td class="right">${shipment.packages}</td>
                </tr>
                <tr>
                    <td colspan="2"><strong>Description:</strong><br/>${shipment.description || 'No description provided.'}</td>
                </tr>
            </tbody>
        </table>
    `}

    <!-- STATUS HISTORY -->
    <div class="section-title">Status History</div>
    ${shipment.statusHistory.map((history) => `
        <div class="history-item">
            <div>
                <div class="h-status">${history.status}</div>
                <div class="h-time">${new Date(history.timestamp).toLocaleString()}</div>
                ${history.note ? `<div class="h-note">Note: ${history.note}</div>` : ''}
            </div>
        </div>
    `).join('')}

    <!-- FOOTER -->
    <div class="doc-footer">
        <p><strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping</p>
        <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
    </div>

    <script>window.onload = function() { window.print(); }</script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('shipments')}</h1>
            <p className="text-gray-600">{t('trackAndManageShipments')}</p>
          </div>

          {user?.role !== 'customer' && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('createShipment')}
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Create New Shipment</DialogTitle>
                <DialogDescription>
                  Add a new shipment with detailed item breakdown and assign it to a trip
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-1">
                <MultiItemShipmentForm
                  customers={customers}
                  trips={trips}
                  warehouses={warehouses}
                  onSubmit={(data) => {
                    createShipment(data);
                    setIsAddDialogOpen(false);
                  }}
                  isSubmitting={false}
                  showTripSelection={true}
                  defaultStatus="Processing"
                />
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search shipments by number, customer, trip, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-1"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center space-x-1"
          >
            <Calendar className="h-4 w-4" />
            <span>History</span>
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                     <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="On the Way to Warehouse">On the Way to Warehouse</SelectItem>
                      <SelectItem value="Processing">Processing</SelectItem>
                      <SelectItem value="In Transit">In Transit</SelectItem>
                      <SelectItem value="At Destination">At Destination</SelectItem>
                      <SelectItem value="Delivered">Delivered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Warehouse</Label>
                  <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {user?.role === 'admin' && (
                        <SelectItem value="all">All Warehouses</SelectItem>
                      )}
                      {warehouseDisplayNames.map(warehouse => (
                        <SelectItem key={warehouse.code} value={warehouse.code}>
                          {warehouse.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Trip</Label>
                  <Select value={tripFilter} onValueChange={setTripFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by trip" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trips</SelectItem>
                      {uniqueTrips.map(trip => (
                        <SelectItem key={trip} value={trip}>
                          {trip}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(activeFilterCount > 0 || searchTerm) && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {filteredShipments.length} shipment{filteredShipments.length !== 1 ? 's' : ''} found
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="flex items-center space-x-1"
                  >
                    <X className="h-4 w-4" />
                    <span>Clear Filters</span>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Date Range History */}
        {showHistory && (
          <Card>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Date Range History</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                    placeholder="Select start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                    placeholder="Select end date"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (dateRange.startDate && dateRange.endDate) {
                      // Filter shipments by date range
                      const filtered = filteredShipments.filter(shipment => {
                        const shipmentDate = new Date(shipment.createdAt);
                        const start = new Date(dateRange.startDate);
                        const end = new Date(dateRange.endDate);
                        end.setHours(23, 59, 59, 999); // Include end date fully
                        return shipmentDate >= start && shipmentDate <= end;
                      });
                      alert(`Found ${filtered.length} shipments between ${dateRange.startDate} and ${dateRange.endDate}`);
                    } else {
                      alert('Please select both start and end dates');
                    }
                  }}
                  className="flex-1"
                >
                  Apply Date Filter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDateRange({ startDate: '', endDate: '' });
                    setShowHistory(false);
                  }}
                >
                  Clear
                </Button>
              </div>
              {/* Summary Statistics */}
              {dateRange.startDate && dateRange.endDate && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Summary Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['Processing', 'In Transit', 'At Destination', 'Delivered'].map(status => {
                      const count = filteredShipments.filter(s => s.status === status).length;
                      return (
                        <div key={status} className="text-center p-3 bg-white rounded border">
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-sm text-gray-600">{status}</div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Additional Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-xl font-bold text-blue-900">
                        {filteredShipments.reduce((sum, s) => sum + (s.weight || 0), 0).toFixed(1)}
                      </div>
                      <div className="text-sm text-blue-700">Total Weight (kg)</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                      <div className="text-xl font-bold text-green-900">
                        {filteredShipments.reduce((sum, s) => sum + (s.packages || 0), 0)}
                      </div>
                      <div className="text-sm text-green-700">Total Packages</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
                      <div className="text-xl font-bold text-purple-900">
                        {filteredShipments.reduce((sum, s) => sum + (s.volume || 0), 0).toFixed(2)}
                      </div>
                      <div className="text-sm text-purple-700">Total Volume (m³)</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Shipment Cards - Shipping Label Style */}
        {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredShipments.map((shipment, index) => (
            <Card 
              key={shipment.id} 
              className="hover:shadow-xl transition-all duration-300 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:scale-[1.02] group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CardContent className="p-4">
                {/* Shipping Label Header */}
                <div className="border-b-2 border-gray-800 pb-2 mb-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <div className="text-sm font-bold text-gray-900 whitespace-nowrap">{shipment.shipmentNumber}</div>
                        <CopyButton text={shipment.shipmentNumber} label="Shipment Number" size="icon" />
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <span className="whitespace-nowrap">Customer: {shipment.customerCode}</span>
                        <CopyButton text={shipment.customerCode} label="Customer Code" size="icon" />
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-2">
                      <QRCodeDisplay
                        value={generateShipmentQRValue(shipment.shipmentNumber)}
                        size={64}
                        showLabel={false}
                      />
                      <Badge className={`${getStatusColor(shipment.status)} text-xs font-bold whitespace-nowrap`}>
                        {shipment.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Shipping Label Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Trip:</span>
                    <span className="text-gray-900 text-right truncate">{shipment.tripName || 'Unassigned'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Weight:</span>
                    <span className="text-gray-900 whitespace-nowrap">{shipment.weight} kg</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Volume:</span>
                    <span className="text-gray-900 whitespace-nowrap">{shipment.volume} m³</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Packages:</span>
                    <span className="text-gray-900 whitespace-nowrap">{shipment.packages}</span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Freight:</span>
                    <Badge className={
                      shipment.freightMethod === 'Air' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }>
                      {shipment.freightMethod}
                    </Badge>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-700 flex-shrink-0">Created:</span>
                    <span className="text-gray-900 text-right text-xs">{shipment.createdAt}</span>
                  </div>
                </div>

                {/* Route Information */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-2">ROUTE</div>
                  <div className="text-sm space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-700 flex-shrink-0 min-w-[40px]">From:</span>
                      <span className="font-medium break-words">{getWarehouseName(shipment.originWarehouse)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-700 flex-shrink-0 min-w-[40px]">To:</span>
                      <span className="font-medium break-words">{getWarehouseName(shipment.destinationWarehouse)}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
                  {user?.role !== 'customer' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingShipment(shipment);
                        setIsEditDialogOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit Shipment"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  <div className="flex space-x-1">
                    {user?.role !== 'customer' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openStatusDialog(shipment)}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Update Status"
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePrintLabel(shipment)}
                      className="text-green-600 hover:text-green-800"
                      title="Print Label"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDetailDialog(shipment)}
                      className="text-blue-600 hover:text-blue-800"
                      title="View Details"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    {user?.role !== 'customer' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteShipment(shipment.shipmentNumber)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Shipment"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredShipments.map((shipment) => (
                      <tr key={shipment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{shipment.shipmentNumber}</div>
                          <div className="text-sm text-gray-500">{shipment.tripName || '—'}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{shipment.customerName}</div>
                          <div className="text-sm text-gray-500 font-mono">{shipment.customerCode}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{shipment.originWarehouse}</div>
                          <div className="text-xs text-gray-500">→ {shipment.destinationWarehouse}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{shipment.weight} kg • {shipment.packages} pkgs</div>
                          <div className="text-sm text-gray-500">{shipment.freightMethod}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(shipment.status)}>
                            {shipment.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailDialog(shipment)}
                              title="View Details"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => printShippingLabel(shipment)}
                              title="Print Label"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {user?.role !== 'customer' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingShipment(shipment);
                                    setIsEditDialogOpen(true);
                                  }}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteShipment(shipment.shipmentNumber)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {filteredShipments.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No shipments found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No shipments match your search criteria.' : ''}
              </p>
              {!searchTerm && user?.role !== 'customer' && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Shipment
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Shipment Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Shipment</DialogTitle>
            <DialogDescription>
              Update shipment information and item details
            </DialogDescription>
          </DialogHeader>
          {editingShipment && (
            <div className="flex-1 overflow-y-auto px-1">
              <MultiItemShipmentForm
                customers={customers}
                trips={trips}
                warehouses={warehouses}
                initialData={editingShipment}
                onSubmit={(data) => {
                  updateShipment({
                    shipment_number: editingShipment.shipmentNumber,
                    ...data
                  });
                  setIsEditDialogOpen(false);
                  setEditingShipment(null);
                }}
                isSubmitting={false}
                showTripSelection={true}
                defaultStatus={editingShipment.status}
              />
            </div>
          )}
          
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Update Shipment Status</DialogTitle>
            <DialogDescription>
              Change the status of {selectedShipmentForStatus?.shipmentNumber}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-status">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Processing">Processing</SelectItem>
                  <SelectItem value="In Transit">In Transit</SelectItem>
                  <SelectItem value="At Destination">At Destination</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-note">Status Note (Optional)</Label>
              <Textarea
                id="status-note"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Add a note about this status change..."
                rows={3}
              />
            </div>

            {selectedShipmentForStatus && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-sm mb-2">Current Status History</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {selectedShipmentForStatus.statusHistory.map((history, index) => (
                    <div key={index} className="text-sm">
                      <div className="flex items-center space-x-2">
                        <Badge className={getStatusColor(history.status)} variant="secondary">
                          {history.status}
                        </Badge>
                        <span className="text-gray-500">
                          {new Date(history.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {history.note && (
                        <div className="text-gray-600 mt-1 ml-1">
                          Note: {history.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleUpdateStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detailed View Dialog - Commercial Invoice Style */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Shipment Details - {selectedShipmentForDetail?.shipmentNumber}</span>
              <Badge className={`${getStatusColor(selectedShipmentForDetail?.status || '')} ml-2`}>
                {selectedShipmentForDetail?.status}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Commercial invoice style detailed shipment information
            </DialogDescription>
          </DialogHeader>

          {selectedShipmentForDetail && (
            <div className="max-h-[70vh] overflow-y-auto">
              {/* Invoice Header */}
              <div className="border-2 border-gray-800 rounded-lg p-6 mb-6 bg-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">SHIPMENT DETAILS</h2>
                    <p className="text-sm text-gray-600">SourceMyGoods Shipping Management System</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{selectedShipmentForDetail.shipmentNumber}</div>
                    <div className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</div>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
                <h3 className="text-lg font-semibold mb-3">CUSTOMER INFORMATION</h3>
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <div><span className="font-medium">Customer Code:</span> {selectedShipmentForDetail.customerCode}</div>
                    <div><span className="font-medium">Customer Name:</span> {selectedShipmentForDetail.customerName}</div>
                    <div><span className="font-medium">Contact:</span> {
                      getCustomerDetails(selectedShipmentForDetail.customerCode)?.phone || 'N/A'
                    }</div>
                  </div>
                  <div>
                    <div><span className="font-medium">Email:</span> {
                      getCustomerDetails(selectedShipmentForDetail.customerCode)?.email || 'N/A'
                    }</div>
                    <div><span className="font-medium">Address:</span> {
                      getCustomerDetails(selectedShipmentForDetail.customerCode)?.address || 'N/A'
                    }</div>
                    <div><span className="font-medium">City:</span> {
                      getCustomerDetails(selectedShipmentForDetail.customerCode)?.city || 'N/A'
                    }</div>
                  </div>
                </div>
              </div>

              {/* Shipment Details */}
              <div className="border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">SHIPMENT DETAILS</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-gray-700">Shipment Number</div>
                    <div className="font-semibold">{selectedShipmentForDetail.shipmentNumber}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Trip Name</div>
                    <div className="font-semibold">{selectedShipmentForDetail.tripName || 'Unassigned'}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Freight Method</div>
                    <Badge className={
                      selectedShipmentForDetail.freightMethod === 'Air' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }>
                      {selectedShipmentForDetail.freightMethod}
                    </Badge>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Status</div>
                    <Badge className={getStatusColor(selectedShipmentForDetail.status)}>
                      {selectedShipmentForDetail.status}
                    </Badge>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Weight</div>
                    <div className="font-semibold">{selectedShipmentForDetail.weight} kg</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Volume</div>
                    <div className="font-semibold">{selectedShipmentForDetail.volume} m³</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Packages</div>
                    <div className="font-semibold">{selectedShipmentForDetail.packages}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Created Date</div>
                    <div className="font-semibold">{selectedShipmentForDetail.createdAt}</div>
                  </div>
                </div>
              </div>

              {/* Items Breakdown */}
              {selectedShipmentForDetail.items && selectedShipmentForDetail.items.length > 0 ? (
                <div className="border border-gray-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3">ITEMS BREAKDOWN</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-900 text-white">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-center">Quantity</th>
                          <th className="px-3 py-2 text-right">Weight (kg)</th>
                          <th className="px-3 py-2 text-right">Volume (m³)</th>
                          <th className="px-3 py-2 text-right">Boxes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedShipmentForDetail.items.map((item, idx) => (
                          <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-3 py-2 border-b">{item.itemNumber || idx + 1}</td>
                            <td className="px-3 py-2 border-b">{item.description}</td>
                            <td className="px-3 py-2 border-b text-center">{item.quantity} {item.quantityUnit}</td>
                            <td className="px-3 py-2 border-b text-right">{item.weight.toFixed(1)}</td>
                            <td className="px-3 py-2 border-b text-right">{item.volume.toFixed(2)}</td>
                            <td className="px-3 py-2 border-b text-right">{item.boxes}</td>
                          </tr>
                        ))}
                        <tr className="bg-blue-100 font-bold">
                          <td colSpan={3} className="px-3 py-2 text-right">TOTALS:</td>
                          <td className="px-3 py-2 text-right">{selectedShipmentForDetail.weight.toFixed(1)} kg</td>
                          <td className="px-3 py-2 text-right">{selectedShipmentForDetail.volume.toFixed(2)} m³</td>
                          <td className="px-3 py-2 text-right">{selectedShipmentForDetail.packages}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold mb-3">DESCRIPTION</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedShipmentForDetail.description || 'No description provided'}
                  </p>
                </div>
              )}

              {/* Status History */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">STATUS HISTORY</h3>
                <div className="space-y-3">
                  {selectedShipmentForDetail.statusHistory.map((history, index) => (
                    <div key={index} className="flex items-start space-x-3 text-sm border-l-2 border-gray-300 pl-4">
                      <div className="flex-shrink-0">
                        <Badge className={getStatusColor(history.status)} variant="secondary">
                          {history.status}
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{history.status}</div>
                        <div className="text-gray-600">{new Date(history.timestamp).toLocaleString()}</div>
                        {history.note && (
                          <div className="text-gray-700 mt-1 bg-blue-50 p-2 rounded text-xs">
                            Note: {history.note}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-300 text-center text-xs text-gray-500">
                This is a computer-generated commercial invoice.
                For any questions regarding this shipment, please contact our customer service.
                Generated on {new Date().toLocaleDateString()} by SourceMyGoods System.
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div className="text-sm text-gray-500">
              Total Weight: {selectedShipmentForDetail?.weight || 0} kg |
              Total Volume: {selectedShipmentForDetail?.volume || 0} m³ |
              Total Packages: {selectedShipmentForDetail?.packages || 0}
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                onClick={() => handlePrintLabel(selectedShipmentForDetail!)}
                className="text-green-600 hover:text-green-800"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Label
              </Button>
              <Button
                variant="outline"
                onClick={() => printCommercialInvoice(selectedShipmentForDetail!)}
                className="text-blue-600 hover:text-blue-800"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Invoice
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownloadPDF(selectedShipmentForDetail!)}
                className="text-green-600 hover:text-green-800"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Invoice
              </Button>
              <Button onClick={() => setIsDetailDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}